import { useGameStore, type GameState } from '../stores/gameStore.js';

/**
 * Hooks granulaires plutôt qu'un seul fourre-tout — les sites consommateurs
 * n'utilisent presque jamais les 4 champs ensemble (voir audit) ; un hook
 * combiné unique aurait fait re-render des composants sur des champs qu'ils
 * n'affichent pas. `useProximityState()` reste dispo pour les sites qui ont
 * réellement besoin de plusieurs champs (ex: VideoTiles.tsx, les 4).
 */
export function useLocalBubbleId(): string | null {
  return useGameStore((s) => s.localBubbleId);
}

/** Type de la bulle locale ('bubble' | 'meeting-room' | ...) — null hors bulle. */
export function useLocalBubbleType(): string | null {
  return useGameStore((s) => s.localBubbleType);
}

/** id (map_zones.id) → nom lisible, pour afficher un nom plutôt qu'un UUID
 *  brut dans le HUD (voir gameStore.ts, bubbleNames). */
export function useBubbleNames(): Map<string, string> {
  return useGameStore((s) => s.bubbleNames);
}

export function usePlayerBubbleIds(): Map<string, string | null> {
  return useGameStore((s) => s.playerBubbleIds);
}

export function useProximityCount(): number {
  return useGameStore((s) => s.proximityCount);
}

export function useProximityUserIds(): string[] {
  return useGameStore((s) => s.proximityUserIds);
}

export function useProximityState(): {
  localBubbleId: string | null;
  playerBubbleIds: Map<string, string | null>;
  proximityCount: number;
  proximityUserIds: string[];
} {
  const localBubbleId = useLocalBubbleId();
  const playerBubbleIds = usePlayerBubbleIds();
  const proximityCount = useProximityCount();
  const proximityUserIds = useProximityUserIds();
  return { localBubbleId, playerBubbleIds, proximityCount, proximityUserIds };
}

/** Bulles verrouillées — domaine séparé (verrouillage), pas proximité/comptage. */
export function useLockedBubbles(): Set<string> {
  return useGameStore((s) => s.lockedBubbles);
}

/** mapZoneId des meeting-rooms en cours d'enregistrement — bandeau RGPD. */
export function useRecordingBubbles(): Set<string> {
  return useGameStore((s) => s.recordingBubbles);
}

/**
 * La bulle actuelle a-t-elle au moins une porte qui la référence ?
 * Calculé côté ZoneScene (seul endroit qui connaît doorZones) sur
 * bubble:changed — voir gameStore.ts. Une bulle sans porte ne peut pas
 * être verrouillée (pas de mur, pas de "toc" possible).
 */
export function useLocalBubbleHasDoor(): boolean {
  return useGameStore((s) => s.localBubbleHasDoor);
}

/** Porte à portée d'interaction du joueur local — null sinon. Écrit par
 *  ZoneScene.checkDoors() via door:nearby / door:left (voir GameBridge.ts). */
export function useNearbyDoor(): GameState['nearbyDoor'] {
  return useGameStore((s) => s.nearbyDoor);
}
